import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { getOfficerById } from "../../../../services/staffingService";
import DashboardSidebar from "../agencyDashboardSidebar";
import OfficerDetailsTab  from "./tabs/details";
import OfficerPrefsTab    from "./tabs/preferences";
import OfficerDocsTab     from "./tabs/documents";
import {
  ArrowLeft, Shield, MapPin, Phone, Mail, Clock, Briefcase,
  Home, FileText, Settings,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/* ---- helpers ---- */
const fmt = (v) => (v == null || v === "" ? "—" : String(v));

const TABS = [
  { id: "details",     label: "Details",     icon: Home },
  { id: "preferences", label: "Preferences", icon: Settings },
  { id: "documents",   label: "Documents",   icon: FileText },
];

export default function OfficerDetail() {
  const { providerCompanyId, officerId } = useParams();
  const history = useHistory();

  const [showSidebar, setShowSidebar] = useState(false);
  const [officerData, setOfficerData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState("details");

  useEffect(() => {
    async function fetchData() {
      if (!providerCompanyId || !officerId) return;
      try {
        const resp = await getOfficerById(providerCompanyId, officerId);
        setOfficerData(resp.data.providerResource);
      } catch (err) {
        console.error("Failed to fetch officer:", err);
        setError(err?.response?.status === 404 ? "Officer not found." : "Failed to load officer data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [providerCompanyId, officerId]);

  /* documents tab mutates officers list state — we normalise that here */
  const handleOfficersUpdate = (updatedOfficers) => {
    if (updatedOfficers && updatedOfficers.length > 0) {
      setOfficerData(updatedOfficers[0]);
    }
  };

  const fullName = officerData
    ? `${officerData.user_id?.firstName || ""} ${officerData.user_id?.lastName || ""}`.trim() ||
      officerData.user_id?.email ||
      "Officer"
    : "";

  const isActive = officerData ? officerData.user_id?.status !== "inactive" : false;

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Back button */}
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/orm`)}
            className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back to Officers
          </button>

          {/* Loading skeleton */}
          {loading && (
            <div className="space-y-4">
              <div className="h-8 w-64 animate-pulse rounded bg-muted" />
              <div className="h-32 animate-pulse rounded-xl bg-muted" />
              <div className="h-10 w-96 animate-pulse rounded bg-muted" />
              <div className="grid grid-cols-2 gap-4">
                <div className="h-48 animate-pulse rounded-xl bg-muted" />
                <div className="h-48 animate-pulse rounded-xl bg-muted" />
              </div>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Main content */}
          {!loading && !error && officerData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* ===== PROFILE HEADER ===== */}
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <div className="border-b border-border bg-primary/5 px-6 py-5">
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-blue-light">
                      {officerData.user_id?.profilePictureUrl ? (
                        <img
                          src={officerData.user_id.profilePictureUrl}
                          alt={fullName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-lg font-semibold text-brand-blue-dark">
                          {`${officerData.user_id?.firstName?.[0] || ""}${officerData.user_id?.lastName?.[0] || ""}`.toUpperCase() || "?"}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-xl font-bold text-card-foreground">{fullName}</h2>
                        {isActive ? (
                          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                            Active
                          </span>
                        ) : (
                          <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {officerData.user_id?.email || "—"}
                        {officerData.servicesOffered?.length > 0 && (
                          <> &middot; {officerData.servicesOffered[0]}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick info chips */}
                <div className="flex flex-wrap gap-2 px-6 py-4">
                  {(officerData.address?.city || officerData.address?.state) && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <MapPin size={12} />
                      {[officerData.address.city, officerData.address.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {officerData.phoneNumber && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <Phone size={12} /> {officerData.phoneNumber}
                    </span>
                  )}
                  {officerData.user_id?.email && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <Mail size={12} /> {officerData.user_id.email}
                    </span>
                  )}
                  {officerData.preferences?.minHourlyRate && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-card-foreground">
                      <Briefcase size={12} /> ${officerData.preferences.minHourlyRate}/hr
                    </span>
                  )}
                </div>
              </div>

              {/* ===== LOCATION NUDGE ===== */}
              {!officerData.address?.street && !officerData.address?.city && !officerData.formattedLocation && (
                <button
                  onClick={() => setActiveTab("details")}
                  className="w-full flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-left transition-colors hover:bg-amber-100"
                >
                  <MapPin size={18} className="mt-0.5 flex-shrink-0 text-amber-600" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Location needed for shift recommendations</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      This officer won't appear in recommended matches until a home address is added. Click to go to Details and add their location.
                    </p>
                  </div>
                </button>
              )}

              {/* ===== TAB BAR ===== */}
              <div className="border-b border-border">
                <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Officer tabs">
                  {TABS.map(({ id: tabId, label, icon: Icon }) => (
                    <button
                      key={tabId}
                      onClick={() => setActiveTab(tabId)}
                      className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                        activeTab === tabId
                          ? "border-brand-pink text-brand-pink"
                          : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                      }`}
                    >
                      <Icon size={15} />
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* ===== TAB CONTENT ===== */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {activeTab === "details" && (
                    <div className="rounded-xl border border-border bg-card shadow-sm p-6">
                      <OfficerDetailsTab
                        officerData={officerData}
                        providerCompanyId={providerCompanyId}
                      />
                    </div>
                  )}

                  {activeTab === "preferences" && (
                    <div className="rounded-xl border border-border bg-card shadow-sm p-6">
                      <OfficerPrefsTab
                        officerData={officerData}
                        providerCompanyId={providerCompanyId}
                      />
                    </div>
                  )}

                  {activeTab === "documents" && (
                    <div className="rounded-xl border border-border bg-card shadow-sm p-6">
                      <OfficerDocsTab
                        officerData={officerData}
                        providerCompanyId={providerCompanyId}
                        officers={[officerData]}
                        setOfficers={handleOfficersUpdate}
                      />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Internal ID */}
              <div className="rounded-lg bg-muted/50 px-4 py-2">
                <p className="text-xs text-muted-foreground">
                  Officer ID: <span className="font-mono">{officerData._id}</span>
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
